/**
 * 聚合器：指纹去重 + 冷却窗口
 *
 * 同一个错误在冷却窗口内只发一条，窗口内的重复次数累加到 pending，
 * 窗口结束时补发一条汇总。避免一次错误爆发把钉钉机器人打到限流封禁
 * （钉钉自定义机器人硬限 20 条/分钟，超限封 10 分钟）。
 */
import { MONITOR_CONFIG } from '@/monitor/config';
import { enqueue, isReporterEnabled } from '@/monitor/core/reporter';
import type { AggregatorEntry, MonitorEvent } from '@/types/monitor';

/**
 * 消息归一化规则：把动态内容替换成占位符，让同类错误收敛为同一指纹。
 * 不做归一化的话，「用户 1024 不存在」和「用户 2048 不存在」会算成两条，去重等于没做。
 * 顺序有意义：先处理长模式（UUID / 查询串 / 引号内容），最后才处理裸数字。
 */
const NORMALIZE_RULES: Array<[RegExp, string]> = [
  [/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '{uuid}'],
  [/\?\S*/g, '?{q}'],
  [/(["'`])(?:(?!\1).)*\1/g, '{s}'],
  [/\d{4,}/g, '{n}'],
];

const normalizeMessage = (message: string): string =>
  NORMALIZE_RULES.reduce((acc, [re, to]) => acc.replace(re, to), message);

/** djb2 hash：这里只需区分不同错误，不需要抗碰撞，故不引入 md5 */
const hash = (input: string): string => {
  let h = 5381;
  for (let i = 0; i < input.length; i += 1) {
    h = (h * 33) ^ input.charCodeAt(i);
  }
  return (h >>> 0).toString(36);
};

/** 取堆栈首个含代码位置的行，作为指纹的一部分 */
const stackLocation = (stack?: string): string =>
  stack
    ?.split('\n')
    .find((line) => line.includes(':'))
    ?.trim() ?? '';

const fingerprint = (event: MonitorEvent): string =>
  hash(
    [
      event.type,
      normalizeMessage(event.message),
      stackLocation(event.stack) || event.source || '',
    ].join('|'),
  );

/** 指纹 → 冷却状态 */
const entries = new Map<string, AggregatorEntry>();

/**
 * 采集入口：首次出现（或冷却已过）立即入队，冷却期内只累加计数。
 */
export const track = (event: MonitorEvent, now = Date.now()): void => {
  /** 有意例外（CLAUDE.md「非必要不加 try/catch」）：采集逻辑出错不能影响业务页面 */
  try {
    if (!isReporterEnabled()) return;
    const fp = fingerprint(event);
    const entry = entries.get(fp);
    if (!entry || now - entry.reportedAt >= MONITOR_CONFIG.DEDUPE_WINDOW) {
      entries.set(fp, { reportedAt: now, pending: 0, sample: event });
      enqueue({ ...event, fingerprint: fp, count: 1 });
      return;
    }
    entry.pending += 1;
  } catch {
    /** 静默降级 */
  }
};

/**
 * 扫描聚合表：冷却结束且期间有新增的补发一条汇总，长期闲置的条目清理掉。
 * 由 index.ts 的定时器以 FLUSH_INTERVAL 频率驱动。
 */
export const sweep = (now = Date.now()): void => {
  /** 有意例外：同 track */
  try {
    entries.forEach((entry, fp) => {
      if (now - entry.reportedAt >= MONITOR_CONFIG.DEDUPE_WINDOW && entry.pending > 0) {
        enqueue({ ...entry.sample, fingerprint: fp, count: entry.pending, timestamp: now });
        entry.reportedAt = now;
        entry.pending = 0;
        return;
      }
      if (now - entry.reportedAt > MONITOR_CONFIG.ENTRY_TTL && entry.pending === 0) {
        entries.delete(fp);
      }
    });
  } catch {
    /** 静默降级 */
  }
};
